type Card = { key: string; rank: string; suit: "clubs" | "diamonds" | "hearts" | "spades" };
type Hand = {
  seatId?: string;
  startingHandIndex?: number;
  cards: Card[];
  id: string;
  outcome?: string;
  value?: number;
};

const SUITS = { clubs: "♣", diamonds: "♦", hearts: "♥", spades: "♠" } as const;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

function card(value: unknown, key: string): Card | null {
  if (!isRecord(value) || typeof value.rank !== "string" || typeof value.suit !== "string") {
    return null;
  }
  if (!(value.suit in SUITS)) return null;
  return { key, rank: value.rank, suit: value.suit as Card["suit"] };
}

function hand(value: unknown): Hand | null {
  if (!isRecord(value) || !Array.isArray(value.cards)) return null;
  let position = 0;
  const cards = value.cards
    .map((item) => {
      position += 1;
      return card(item, `${position}`);
    })
    .filter((item): item is Card => item !== null);
  if (cards.length === 0) return null;
  return {
    cards,
    id: typeof value.handId === "string" ? value.handId : JSON.stringify(cards),
    ...(typeof value.outcome === "string" ? { outcome: value.outcome } : {}),
    ...(typeof value.value === "number" ? { value: value.value } : {}),
  };
}

function parse(state: unknown): { dealer: Hand; players: Hand[] } | null {
  if (!isRecord(state) || state.game !== "blackjack" || !isRecord(state.dealer)) return null;
  const dealer = hand(state.dealer);
  if (!dealer || !Array.isArray(state.seats)) return null;
  const players = state.seats.flatMap((seat) =>
    isRecord(seat) && Array.isArray(seat.hands)
      ? seat.hands
          .map(hand)
          .filter((item): item is Hand => item !== null)
          .map((item, index) => ({
            ...item,
            ...(typeof seat.seatId === "string" ? { seatId: seat.seatId } : {}),
            startingHandIndex: index + 1,
          }))
      : [],
  );
  return players.length ? { dealer, players } : null;
}

function PlayingCard({ value }: { value: Card }) {
  const red = value.suit === "diamonds" || value.suit === "hearts";
  return (
    <span
      aria-label={`${value.rank} of ${value.suit}`}
      className={`playing-card${red ? " playing-card-red" : ""}`}
      role="img"
    >
      <span>{value.rank}</span>
      <span aria-hidden="true">{SUITS[value.suit]}</span>
    </span>
  );
}

function HandView({ label, value }: { label: string; value: Hand }) {
  return (
    <div className="blackjack-hand">
      <div className="blackjack-hand-head">
        <strong>{label}</strong>
        <span className="blackjack-hand-total">
          {value.value ?? "—"}
          {value.outcome ? (
            <span className={`badge hand-${value.outcome}`}>{value.outcome}</span>
          ) : null}
        </span>
      </div>
      <div className="playing-cards">
        {value.cards.map((item) => (
          <PlayingCard key={item.key} value={item} />
        ))}
      </div>
    </div>
  );
}

export function BlackjackRound({ terminalState }: { terminalState: unknown }) {
  const state = parse(terminalState);
  if (!state) {
    return (
      <div className="legacy-round-note" role="note">
        <strong>Card history unavailable</strong>
        <span className="legacy-round-copy">
          This legacy round did not record a replayable final hand.
        </span>
      </div>
    );
  }

  return (
    <section aria-label="Blackjack table result" className="blackjack-result">
      <HandView label="Dealer" value={state.dealer} />
      <div className="blackjack-divider">Final hands</div>
      {state.players.map((player, index) => (
        <HandView
          key={`${player.seatId}:${player.id}`}
          label={
            player.seatId
              ? `${player.seatId} · Hand ${player.startingHandIndex}`
              : state.players.length > 1
                ? `Player hand ${index + 1}`
                : "Player"
          }
          value={player}
        />
      ))}
    </section>
  );
}
